/**
 * Push notifications registration — Capacitor bridge.
 *
 * Flow (when enabled):
 *  1. On native app boot, request permission
 *  2. Register with APNs (iOS) or FCM (Android) → receive device token
 *  3. POST the token + platform to backend so it can address notifications
 *
 * IMPORTANT — currently DISABLED by default:
 *  Calling PushNotifications.register() on Android requires a valid
 *  google-services.json + Firebase project. Without one, the native call
 *  crashes the WebView renderer (shows "Bug in the app · Clear cache").
 *  Enable by setting REACT_APP_PUSH_ENABLED=true in frontend/.env after FCM
 *  is provisioned. Until then we no-op silently on native.
 */
import { isNative, platform } from "./platform";
import { api } from "./api";

const PUSH_ENABLED = process.env.REACT_APP_PUSH_ENABLED === "true";

export async function initPushNotifications() {
  if (!isNative()) return { skipped: "web" };
  if (!PUSH_ENABLED) {
    // Deliberately skip on native until FCM is configured — see file header.
    console.info("[push] disabled (REACT_APP_PUSH_ENABLED != 'true')");
    return { skipped: "disabled" };
  }

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== "granted") return { denied: true };
    }

    await PushNotifications.register();

    PushNotifications.addListener("registration", async (t) => {
      try {
        await api.post("/push/register", { token: t.value, platform: platform() });
      } catch (err) {
        console.warn("Push token upload failed:", err?.message || err);
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.warn("Push registration error:", err);
    });

    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      // TODO: surface toast / route to relevant screen
      console.info("Push received:", notification);
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      // TODO: deep-link into the app based on payload
      console.info("Push tapped:", action);
    });

    return { registered: true };
  } catch (err) {
    console.error("Push init failed:", err);
    return { error: err?.message || String(err) };
  }
}
